// Scroll utilities for better navigation experience
import React from 'react';

/**
 * Smoothly scrolls to the top of the page
 * @param {string} behavior - 'smooth' or 'auto'
 */
export const scrollToTop = (behavior = 'smooth') => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: behavior
  });
};

/**
 * Scrolls to a specific element by ID
 * @param {string} elementId - The ID of the element to scroll to
 * @param {number} offset - Optional offset from the top (default: 80px for header)
 * @param {string} behavior - 'smooth' or 'auto'
 */
export const scrollToElement = (elementId, offset = 80, behavior = 'smooth') => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: behavior
    });
  }
};

/**
 * Scrolls to a specific Y position
 * @param {number} position - Y position to scroll to
 * @param {string} behavior - 'smooth' or 'auto'
 */
export const scrollToPosition = (position, behavior = 'smooth') => {
  window.scrollTo({
    top: position,
    left: 0,
    behavior: behavior
  });
};

/**
 * Gets the current scroll position
 * @returns {number} Current Y scroll position
 */
export const getCurrentScrollPosition = () => {
  return window.pageYOffset || document.documentElement.scrollTop;
};

/**
 * Checks if the user has scrolled past a certain threshold
 * @param {number} threshold - The scroll threshold (default: 100px)
 * @returns {boolean} True if scrolled past threshold
 */
export const hasScrolledPastThreshold = (threshold = 100) => {
  return getCurrentScrollPosition() > threshold;
};

/**
 * Debounced scroll handler to improve performance
 * @param {Function} callback - Function to call on scroll
 * @param {number} delay - Delay in milliseconds (default: 100ms)
 * @returns {Function} Debounced scroll handler
 */
export const createDebouncedScrollHandler = (callback, delay = 100) => {
  let timeoutId;
  
  return () => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(callback, delay);
  };
};

/**
 * Hook for scroll-to-top functionality on route changes
 * Should be used in components that need to reset scroll position
 */
export const useScrollToTopOnMount = () => {
  React.useEffect(() => {
    scrollToTop('auto'); // Instant scroll on mount
  }, []);
};

/**
 * Smooth scroll to top with fade effect
 * Useful for "back to top" buttons
 */
export const smoothScrollToTopWithFade = () => {
  const scrollStep = -window.scrollY / (500 / 15); // 500ms duration
  
  const scrollInterval = setInterval(() => {
    if (window.scrollY !== 0) {
      window.scrollBy(0, scrollStep);
    } else {
      clearInterval(scrollInterval);
    }
  }, 15);
};

/**
 * Checks if an element is in the viewport
 * @param {HTMLElement} element - The element to check
 * @param {number} threshold - Percentage of element that should be visible (0-1)
 * @returns {boolean} True if element is in viewport
 */
export const isElementInViewport = (element, threshold = 0.1) => {
  if (!element) return false;
  
  const rect = element.getBoundingClientRect();
  const elementHeight = rect.bottom - rect.top;
  const visibleHeight = Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);
  
  return visibleHeight / elementHeight >= threshold;
};

/**
 * Saves the current scroll position to sessionStorage
 * @param {string} key - Storage key (default: 'scrollPosition')
 */
export const saveScrollPosition = (key = 'scrollPosition') => {
  sessionStorage.setItem(key, getCurrentScrollPosition().toString());
};

/**
 * Restores scroll position from sessionStorage
 * @param {string} key - Storage key (default: 'scrollPosition')
 * @param {boolean} smooth - Whether to use smooth scrolling
 */
export const restoreScrollPosition = (key = 'scrollPosition', smooth = false) => {
  const savedPosition = sessionStorage.getItem(key);
  if (savedPosition) {
    const position = parseInt(savedPosition, 10);
    scrollToPosition(position, smooth ? 'smooth' : 'auto');
    sessionStorage.removeItem(key); // Clean up after use
  }
};

/**
 * Creates a scroll spy for navigation highlighting
 * @param {Array} sectionIds - Array of section IDs to watch
 * @param {Function} callback - Callback function that receives the active section ID
 * @param {number} offset - Offset from top for activation (default: 100px)
 */
export const createScrollSpy = (sectionIds, callback, offset = 100) => {
  const handleScroll = createDebouncedScrollHandler(() => {
    const scrollPosition = getCurrentScrollPosition() + offset;
    
    for (let i = sectionIds.length - 1; i >= 0; i--) {
      const element = document.getElementById(sectionIds[i]);
      if (element && element.offsetTop <= scrollPosition) {
        callback(sectionIds[i]);
        break;
      }
    }
  }, 50);

  window.addEventListener('scroll', handleScroll);
  
  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

export default {
  scrollToTop,
  scrollToElement,
  scrollToPosition,
  getCurrentScrollPosition,
  hasScrolledPastThreshold,
  createDebouncedScrollHandler,
  useScrollToTopOnMount,
  smoothScrollToTopWithFade,
  isElementInViewport,
  saveScrollPosition,
  restoreScrollPosition,
  createScrollSpy
};
